import React, { useState, useRef, useCallback } from 'react';
import useDashboardStore from '../store/dashboardStore';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import FBACalculator from '../components/FBACalculator';

// PERFORMANCE: Simple in-memory cache for barcode scans to avoid duplicate API calls
const SCAN_CACHE_MAX_SIZE = 50;
const SCAN_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const Scan = () => {
  const { products, productsLoading, productsError, scanByBarcode } = useDashboardStore();
  const [barcode, setBarcode] = useState('');
  const [cacheHit, setCacheHit] = useState(false);

  // PERFORMANCE: Cache ref to persist across renders
  const scanCacheRef = useRef(new Map());

  // PERFORMANCE: Clean up expired cache entries
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    const cache = scanCacheRef.current;

    for (const [key, entry] of cache.entries()) {
      if (now - entry.timestamp > SCAN_CACHE_TTL_MS) {
        cache.delete(key);
      }
    }

    // Remove oldest entries if cache is too large
    while (cache.size > SCAN_CACHE_MAX_SIZE) {
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = barcode.trim();
    if (!code) return;

    // PERFORMANCE: Check cache first
    const cache = scanCacheRef.current;
    const cachedEntry = cache.get(code);

    if (cachedEntry && Date.now() - cachedEntry.timestamp < SCAN_CACHE_TTL_MS) {
      // Cache hit - use cached data
      setCacheHit(true);
      // Update store with cached products directly
      useDashboardStore.setState({
        products: cachedEntry.products,
        productsLoading: false,
        productsError: null,
      });
      return;
    }

    setCacheHit(false);
    cleanupCache();

    // Cache miss - make API call
    await scanByBarcode(code);

    // Store result in cache after scan completes
    const currentProducts = useDashboardStore.getState().products;
    if (currentProducts.length > 0) {
      cache.set(code, {
        products: currentProducts,
        timestamp: Date.now(),
      });
    }
  };

  return (
    <div>
      <div style={{
        background: 'rgba(10, 10, 15, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: '20px',
        padding: '20px',
        marginBottom: '20px',
      }}>
        <h2 style={{
          color: '#00d4ff',
          fontFamily: 'Orbitron, monospace',
          marginBottom: '15px',
          fontSize: '1.2rem',
        }}>
          Barcode Scan (EAN/UPC)
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter or paste barcode…"
            aria-label="Barcode"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'rgba(102, 126, 234, 0.1)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '1rem',
            }}
          />
          <button type="submit" className="quantum-btn" style={{ padding: '12px 24px' }}>
            Scan
          </button>
        </form>
        <div aria-live="polite" style={{ marginTop: 10, color: 'rgba(255,255,255,0.7)' }}>
          {productsLoading ? 'Scanning…' : `Results: ${products.length}`}
          {cacheHit && !productsLoading && products.length > 0 && (
            <span style={{ marginLeft: '10px', color: '#22c55e', fontSize: '0.85rem' }}>
              (cached)
            </span>
          )}
        </div>
        {productsError && (
          <div role="alert" style={{
            marginTop: '12px', padding: '10px 12px',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px', color: '#fca5a5'
          }}>
            {productsError}
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px',
      }}>
        {productsLoading ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <Skeleton lines={6} height={16} />
          </div>
        ) : products.length > 0 ? (
          products.map((product, index) => (
            <div
              key={index}
              style={{
                background: 'rgba(10, 10, 15, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '20px',
                overflow: 'hidden',
                transition: 'all 0.3s',
              }}
            >
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.title || 'Product'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div style={{ padding: 20 }}>
                <h4 style={{ color: 'white', marginBottom: 10, fontSize: '1.1rem', fontWeight: 600 }}>
                  {product.title || product.name || 'Unknown Product'}
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', marginBottom: 12 }}>
                  {product.marketplace || 'Marketplace'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#22c55e', fontSize: '1.4rem', fontWeight: 'bold', fontFamily: 'Orbitron, monospace' }}>
                    ${product.price || '0.00'}
                  </span>
                  <button className="quantum-btn" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                    Track Price
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState icon="🧾" title="No scan results" description="Enter a barcode above to look up products." />
          </div>
        )}
      </div>

      {/* FBA profitability check for the scanned item (prefills first result's price) */}
      {products.length > 0 && !productsLoading && (
        <div style={{ marginTop: 20 }}>
          <FBACalculator salePrice={parseFloat(products[0]?.price) > 0 ? parseFloat(products[0].price) : undefined} />
        </div>
      )}
    </div>
  );
};

export default Scan;

